"use client";

import { useMemo } from "react";
import { devLabel } from "@/lib/dev-labels";
import { CATEGORIES, WIZARDS, type Wizard } from "./wizards-data";
import { WizardCard } from "./WizardCard";
import styles from "./MagicWandModal.module.css";

/**
 * WizardGrid — Phase 25-AB Task 14.
 *
 * Mockup source: docs/mockups/magic-wand.html (panel 28-B .. 28-J).
 *
 * Groups all 50 wizards by category and renders one .ai-grid block per
 * group. Supports optional text-search filtering and a wizard-key
 * predicate so the parent can restrict which wizards render (e.g.
 * "free only" filter).
 */

export interface WizardGridProps {
	onRunWizard: (key: string) => void;
	query?: string;
	filter?: (w: Wizard) => boolean;
}

function matchesQuery(w: Wizard, q: string): boolean {
	if (!q) return true;
	const needle = q.toLowerCase();
	return (
		w.key.toLowerCase().includes(needle) ||
		w.name.toLowerCase().includes(needle) ||
		w.tag.toLowerCase().includes(needle) ||
		w.description.toLowerCase().includes(needle)
	);
}

export function WizardGrid({ onRunWizard, query = "", filter }: WizardGridProps) {
	const filtered = useMemo(() => {
		return WIZARDS.filter(
			(w) => matchesQuery(w, query) && (!filter || filter(w)),
		);
	}, [query, filter]);

	return (
		<div data-testid="wizard-grid">
			{CATEGORIES.map((cat) => {
				const items = filtered.filter((w) => w.category === cat.key);
				if (items.length === 0) return null;
				return (
					<section
						key={cat.key}
						aria-labelledby={`mw-cat-${cat.key}`}
						data-testid={`wizard-category-${cat.key}`}
						{...devLabel(`28-${cat.key}`)}
					>
						<div className={styles.groupTitle}>
							<span
								id={`mw-cat-${cat.key}`}
								className={styles.groupLabel}
							>
								{cat.name}
							</span>
							<span className={styles.groupCount}>
								{cat.hint} · {items.length} 종
							</span>
						</div>
						<div className={styles.aiGrid}>
							{items.map((w) => (
								<WizardCard
									key={w.key}
									wizard={w}
									onRun={onRunWizard}
								/>
							))}
						</div>
					</section>
				);
			})}
			{filtered.length === 0 && (
				<div className={styles.emptyState} data-testid="wizard-grid-empty">
					검색 결과 없음
				</div>
			)}
		</div>
	);
}
